import cldrSearchIndex from '../data/emojiCldrSearchIndex.json';
import { activityPlusEmoticonKeywordEntries } from '../data/activityPlusEmoticonKeywords';

/**
 * English CLDR annotation keywords (emojibase-data 16.0.3), plus the hand-tuned
 * words in activityPlusEmoticonKeywordLibrary.json. Skin-tone components and
 * bare regional indicators are omitted. The nine random tiles still come from
 * the activity list; this index is search-only.
 */
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'of', 'with', 'without', 'in', 'on', 'to', 'for', 'from', 'at',
]);

const emojiSearchKey = (emoji) => String(emoji).replace(/\uFE0F/g, '');

const addWords = (target, text) => {
  const parts = String(text).toLowerCase().split(/[^a-z0-9]+/);
  for (const part of parts) {
    if (part.length < 2 || STOP_WORDS.has(part)) continue;
    target.add(part);
  }
};

const queryTokens = (query) => {
  const tokens = [];
  const seen = new Set();
  const parts = String(query ?? '').toLowerCase().trim().split(/[^a-z0-9]+/);
  for (const part of parts) {
    if (part.length < 2 || STOP_WORDS.has(part) || seen.has(part)) continue;
    seen.add(part);
    tokens.push(part);
  }
  return tokens;
};

const searchEntries = (() => {
  const byKey = new Map();
  for (const row of cldrSearchIndex) {
    const key = emojiSearchKey(row.emoji);
    if (byKey.has(key)) continue;
    byKey.set(key, { emoji: row.emoji, words: new Set(row.words) });
  }
  for (const custom of activityPlusEmoticonKeywordEntries) {
    const key = emojiSearchKey(custom.emoji);
    let entry = byKey.get(key);
    if (!entry) {
      entry = { emoji: custom.emoji, words: new Set() };
      byKey.set(key, entry);
    } else {
      entry.emoji = custom.emoji;
    }
    addWords(entry.words, custom.officialName);
    for (const keyword of custom.keywords || []) addWords(entry.words, keyword);
  }
  const ordered = [];
  const used = new Set();
  for (const row of cldrSearchIndex) {
    const key = emojiSearchKey(row.emoji);
    if (used.has(key) || !byKey.has(key)) continue;
    used.add(key);
    ordered.push(byKey.get(key));
  }
  for (const [key, entry] of byKey) {
    if (!used.has(key)) ordered.push(entry);
  }
  return ordered;
})();

/**
 * Whole-word match: every query word must equal a CLDR annotation word or a
 * hand-tuned keyword. Returns up to maxResults real hits in catalog order.
 * Already-shown emojis in `exclude` are skipped so MORE can page. No random fill.
 */
export function searchActivityPlusEmojisByQuery(query, maxResults = 9, exclude = []) {
  const tokens = queryTokens(query);
  if (!tokens.length || maxResults <= 0) return [];
  const excludeKeys = new Set();
  for (const emoji of exclude) {
    excludeKeys.add(emoji);
    excludeKeys.add(emojiSearchKey(emoji));
  }
  const out = [];
  for (const entry of searchEntries) {
    const key = emojiSearchKey(entry.emoji);
    if (excludeKeys.has(entry.emoji) || excludeKeys.has(key)) continue;
    if (!tokens.every((token) => entry.words.has(token))) continue;
    out.push(entry.emoji);
    if (out.length >= maxResults) break;
  }
  return out;
}
